import { useEffect, useState } from 'react';
import { Input, Popconfirm, Radio, Table } from 'antd';
import { ModalComponent } from '../baseComponents';
import {
  AlertIcon,
  BinIcon,
  CloseAiIcon,
  EditableIcon,
  UpdateIcon,
} from 'src/icons';
import { useDispatch } from 'react-redux';
import {
  addRecentSearchAction,
  getRecentSearchAction,
} from 'src/store/actions/appointment.action';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';
import { useSelector } from 'react-redux';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

export const ManageViewModal = ({
  isMangeViewOpen,
  handleCloseManageView,
  recentSearchList,
  setRenameTitle,
  selectedTitle,
  setSelectedTitle,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const isMobile = window.innerWidth < 1260;

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const [showError, setShowError] = useState(false);

  const handleRenameEditAppointment = (event) => {
    const value = event.target.value;
    setEditedTitle(value);
    if (value === '') {
      setShowError(true);
    } else {
      setShowError(false);
    }
  };

  useEffect(() => {
    if (isMangeViewOpen) {
      dispatch(getRecentSearchAction('appointments'));
    }
  }, [isMangeViewOpen, dispatch]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  const handleEditClick = (record) => {
    setIsEditing(record.id);
    setEditedTitle(record.title);
  };

  const handleRadioChange = async (selectedRecord) => {
    const updatedSearches = (recentSearchList?.search_preference || []).map(
      (record) => {
        if (record.id === selectedRecord.id) {
          return { ...record, isDefaultAppointmentView: true };
        }

        return { ...record, isDefaultAppointmentView: false };
      }
    );

    dispatch(addRecentSearchAction(updatedSearches, 'appointments'));

    showToastSuccess('Default preference updated');
  };

  const handleTickClick = (record) => {
    analytics.track('Clicked Edit This View In Recent Searches', {
      title: record?.title,
      email: user?.email,
    });
    const updatedRecordIndex = recentSearchList.search_preference.findIndex(
      (item) => item.id === record.id
    );
    if (updatedRecordIndex !== -1) {
      const updatedSearchPreference = recentSearchList.search_preference.map(
        (item, index) => {
          if (index === updatedRecordIndex) {
            return { ...item, title: editedTitle };
          }
          return item;
        }
      );
      dispatch(addRecentSearchAction(updatedSearchPreference, 'appointments'));
      setIsEditing(false);
      if (selectedTitle === record?.title) {
        setRenameTitle(editedTitle);
      }
      showToastSuccess(`View renamed successfully.`);
    } else {
      console.error('Record not found');
    }
  };

  const handleDelete = (record) => {
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Delete This View In Recent Searches', {
      title: record?.title,
      email: user?.email,
    });

    const updatedSearchPreference = recentSearchList.search_preference.filter(
      (item) => item.id !== record.id
    );

    dispatch(addRecentSearchAction(updatedSearchPreference, 'appointments'))
      .then(() => {
        if (selectedTitle === record?.title) {
          setRenameTitle(null);
          setSelectedTitle('');
          localStorage.removeItem(
            `${currentPractitioner?.org_uuid}_selectedTitle`
          );
        }
        showToastSuccess(`View deleted successfully.`);
      })
      .catch(() => showToastError('Failed to delete the view.'));
  };

  const handleXClick = () => {
    setIsEditing(false);
  };

  const columnsDesktop = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (text, record) => (
        <div className="">
          {isEditing === record.id ? (
            <div className="">
              <div className="flex gap-2 items-center">
                <div className="flex items-center border border-[#e5e8eb] rounded-lg">
                  <Input
                    id="rename"
                    bordered={false}
                    value={editedTitle}
                    onChange={handleRenameEditAppointment}
                    className="bg-white shadow-md"
                  />
                </div>
                <div
                  id="updateIcon"
                  className="bg-[#00d091] rounded-full flex items-center justify-center p-1"
                >
                  <UpdateIcon
                    onClick={() =>
                      editedTitle.trim() !== ''
                        ? handleTickClick(record)
                        : setShowError(true)
                    }
                  />
                </div>
                <div
                  id="closeIcon"
                  className="ml-2 bg-[#d63232] rounded-full flex items-center justify-center p-1"
                >
                  <CloseAiIcon
                    stroke="#ffffff"
                    fill="#ffffff"
                    onClick={handleXClick}
                  />
                </div>
              </div>
              {showError &&
                isEditing === record.id &&
                !editedTitle.length > 0 && (
                  <div className="text-red-500 text-xs mt-1">
                    Title cannot be empty.
                  </div>
                )}
            </div>
          ) : (
            <div className="flex items-center">
              <div>{text}</div>
              <div className="ml-5">
                <EditableIcon onClick={() => handleEditClick(record)} />
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Filters',
      dataIndex: 'variables',
      key: 'variables',
    },
    {
      title: 'Default View',
      dataIndex: '',
      key: 'actions',
      render: (text, record) => {
        return (
          <div className="flex space-x-6 ml-8">
            <Radio
              checked={record?.isDefaultAppointmentView}
              onChange={() => handleRadioChange(record)}
            />
          </div>
        );
      },
    },
    {
      title: <div className=""></div>,
      key: 'actions',
      render: (text, record) => (
        <div className="flex space-x-6 manage_popup">
          <Popconfirm
            title={
              <span className="font-medium text-medium pb-5">
                {' '}
                Are you sure to delete this view?
              </span>
            }
            bordered={false}
            icon={<AlertIcon className={'mr-3'} />}
            onConfirm={() => handleDelete(record)}
            okText={
              <span className=" bg-[#d63232] px-5 py-1 rounded-md">Yes</span>
            }
            cancelText={
              <span className="bg-gray-400 px-5 py-1 rounded-md text-white hover:text-white">
                No
              </span>
            }
            okButtonProps={{
              className: 'text-medium font-medium border-none !bg-transparent',
            }}
            cancelButtonProps={{
              className:
                'text-medium font-medium border-none !bg-transparent -mr-3',
            }}
            overlayClassName="manage_popup "
          >
            <BinIcon />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const columnsMobile = [
    {
      title: <div className=""></div>,
      dataIndex: 'title',
      key: 'title',
      width: 150,
      render: (text, record) => (
        <div className="">
          {isEditing === record.id ? (
            <div className="">
              <div className="flex gap-2 items-center">
                <div className="flex items-center border border-[#e5e8eb] rounded-lg">
                  <Input
                    id="rename"
                    bordered={false}
                    value={editedTitle}
                    onChange={handleRenameEditAppointment}
                    className="bg-white shadow-md"
                  />
                </div>
                <div
                  id="updateIcon"
                  className="bg-[#00d091] rounded-full flex items-center justify-center p-1"
                >
                  <UpdateIcon
                    onClick={() =>
                      editedTitle.trim() !== ''
                        ? handleTickClick(record)
                        : setShowError(true)
                    }
                  />
                </div>
                <div
                  id="closeIcon"
                  className="ml-2 bg-[#d63232] rounded-full flex items-center justify-center p-1"
                >
                  <CloseAiIcon
                    stroke="#ffffff"
                    fill="#ffffff"
                    onClick={handleXClick}
                  />
                </div>
              </div>
              {showError &&
                isEditing === record.id &&
                !editedTitle.length > 0 && (
                  <div className="text-red-500 text-xs mt-1">
                    Title cannot be empty.
                  </div>
                )}
            </div>
          ) : (
            <div className="">
              <div className="flex gap-2 items-center ">
                <div className="text-md font-semibold">Title:</div>
                <div className="flex items-center">
                  <div>{text}</div>
                </div>

                <div className="ml-2">
                  <EditableIcon onClick={() => handleEditClick(record)} />
                </div>
              </div>
            </div>
          )}
          <div className="mt-2 flex gap-2">
            <div className="text-md font-semibold">Filters: </div>
            <div className="flex flex-wrap text-wrap">
              {record?.practitioners?.map((practitioner, innerIndex) => (
                <span key={innerIndex} className="whitespace-normal">
                  {practitioner?.practitionerName +
                    (innerIndex < record.practitioners?.length - 1
                      ? ', '
                      : ' ')}
                </span>
              ))}
              {record?.patient && <div>/ {record?.patient}</div>}
              {record?.date && <div>/ {record?.date}</div>}
              {record?.status ? (
                <div>
                  / {record?.status === 'New' ? 'Not started' : record?.status}
                </div>
              ) : (
                <div>/ All</div>
              )}
            </div>
          </div>
        </div>
      ),
    },
    // {
    //     title: 'Filters',
    //     dataIndex: 'variables',
    //     key: 'variables',
    // },
    {
      title: 'Default View',
      dataIndex: '',
      key: 'actions',
      render: (text, record) => {
        return (
          <div className="flex space-x-6 ml-8">
            <Radio
              checked={record?.isDefaultAppointmentView}
              onChange={() => handleRadioChange(record)}
            />
          </div>
        );
      },
    },
    {
      title: <div className=""></div>,
      key: 'actions',
      render: (text, record) => (
        <div className="flex space-x-3 manage_popup">
          <Popconfirm
            title={
              <span className="font-medium text-medium pb-5">
                {' '}
                Are you sure to delete this view?
              </span>
            }
            bordered={false}
            icon={<AlertIcon className={'mr-3'} />}
            onConfirm={() => handleDelete(record)}
            okText={
              <span className=" bg-[#d63232] px-5 py-1 rounded-md">Yes</span>
            }
            cancelText={
              <span className="bg-gray-400 px-5 py-1 rounded-md text-white hover:text-white">
                No
              </span>
            }
            okButtonProps={{
              className: 'text-medium font-medium !border-none !bg-transparent',
            }}
            cancelButtonProps={{
              className:
                'text-medium font-medium !border-none !bg-transparent -mr-3',
            }}
            overlayClassName="manage_popup "
          >
            <BinIcon />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const data = Array.isArray(recentSearchList?.search_preference)
    ? recentSearchList.search_preference.map((search) => ({
        key: search.title,
        ...search,
        variables: (
          <div className="flex flex-wrap text-wrap">
            {search?.practitioners?.map((practitioner, innerIndex) => (
              <span key={innerIndex} className="whitespace-normal">
                {practitioner?.practitionerName +
                  (innerIndex < search.practitioners?.length - 1 ? ', ' : ' ')}
              </span>
            ))}
            {search?.patient && <div>/ {search?.patient}</div>}
            {search?.date && <div>/ {search?.date}</div>}
            {search?.status ? <div>/ {search?.status}</div> : <div>/ All</div>}
          </div>
        ),
      }))
    : [];
  return (
    <>
      <ModalComponent
        centered={true}
        open={isMangeViewOpen}
        handleCancel={handleCloseManageView}
        submitButtonText=""
        cancelButtonText="Cancel"
        closable={true}
        className={'notificationHistory-page-table'}
        additionalBody={
          <>
            <div className="mb-5 font-medium text-xl">Manage views</div>
            <div className="ant-table rounded-xl shadow-lg">
              <Table
                showHeader={isMobile ? false : true}
                columns={isMobile ? columnsMobile : columnsDesktop}
                className={'notification-table-wrapper'}
                dataSource={data}
                pagination={false}
              />
            </div>
          </>
        }
      />
    </>
  );
};
